/**
 * Pagination primitives.
 *
 * The backend returns `PagedResultsDefinition<T>`:
 * `{ items, count, pageNumber, pageSize, hasNextPage, hasPreviousPage }`.
 * **`pageNumber` is 1-based** and `pageSize` must be within 1–100
 * (see Ticketon.Rewards README §4). Every list query shares these shapes.
 */

export type SortDirection = "ASC" | "DESC";

/**
 * A single sort instruction. Not sent to the backend — no rewards list query
 * accepts a sort argument; kept for UI-side sorting state.
 */
export interface ISort<TField extends string = string> {
  field: TField;
  direction: SortDirection;
}

/** One-based page request. */
export interface IPageRequest {
  /** 1-based page number. */
  pageNumber: number;
  /** Items per page, 1–{@link MAX_PAGE_SIZE}. */
  pageSize: number;
}

/** A page of results returned from the backend. */
export interface IPage<T> {
  items: T[];
  /** Total number of items across all pages. */
  count: number;
  /** 1-based page number. */
  pageNumber: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/** The first page number (pages are 1-based). */
export const FIRST_PAGE_NUMBER = 1;

/** Smallest page size the backend accepts. */
export const MIN_PAGE_SIZE = 1;

/** Largest page size the backend accepts. */
export const MAX_PAGE_SIZE = 100;

/** Default page size used by rewards list screens. */
export const DEFAULT_PAGE_SIZE = 20;

/** Default page request (first page, default size). */
export const DEFAULT_PAGE_REQUEST: IPageRequest = {
  pageNumber: FIRST_PAGE_NUMBER,
  pageSize: DEFAULT_PAGE_SIZE,
};

/** Clamps a page size into the range the backend accepts (1–100). */
export function clampPageSize(pageSize: number): number {
  if (!Number.isFinite(pageSize)) return DEFAULT_PAGE_SIZE;
  return Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, Math.trunc(pageSize)));
}

/** Clamps a page number to a valid 1-based page. */
export function clampPageNumber(pageNumber: number): number {
  if (!Number.isFinite(pageNumber)) return FIRST_PAGE_NUMBER;
  return Math.max(FIRST_PAGE_NUMBER, Math.trunc(pageNumber));
}

/** Returns a request with page number and size clamped to valid values. */
export function normalizePageRequest(request: IPageRequest): IPageRequest {
  return {
    pageNumber: clampPageNumber(request.pageNumber),
    pageSize: clampPageSize(request.pageSize),
  };
}

/** Total number of pages for a given total count and page size. */
export function totalPages(count: number, pageSize: number): number {
  if (pageSize <= 0) return 0;
  return Math.ceil(count / pageSize);
}

type PagePosition = Pick<IPage<unknown>, "count" | "pageNumber" | "pageSize"> &
  Partial<Pick<IPage<unknown>, "hasNextPage">>;

/** Whether a next page exists. Prefers the server-provided flag when present. */
export function hasNextPage(page: PagePosition): boolean {
  if (typeof page.hasNextPage === "boolean") return page.hasNextPage;
  return page.pageNumber * page.pageSize < page.count;
}

/** Whether a previous page exists. Prefers the server-provided flag when present. */
export function hasPreviousPage(
  page: Pick<IPage<unknown>, "pageNumber"> & Partial<Pick<IPage<unknown>, "hasPreviousPage">>,
): boolean {
  if (typeof page.hasPreviousPage === "boolean") return page.hasPreviousPage;
  return page.pageNumber > FIRST_PAGE_NUMBER;
}

/** An empty first page, useful for error fallbacks and initial state. */
export function emptyPage<T>(pageSize = DEFAULT_PAGE_SIZE): IPage<T> {
  return {
    items: [],
    count: 0,
    pageNumber: FIRST_PAGE_NUMBER,
    pageSize: clampPageSize(pageSize),
    hasNextPage: false,
    hasPreviousPage: false,
  };
}

/** An inclusive date range expressed as ISO-8601 date strings. */
export interface IDateRange {
  /** ISO-8601 date or date-time, inclusive lower bound. */
  from: string;
  /** ISO-8601 date or date-time, inclusive upper bound (a date-only value means end of that day). */
  to: string;
}
